// src/ManifestScreen.jsx
import { useState, useEffect, useRef } from "react";
import { api } from "./api/api";

// Data for Point of Loading autocomplete
const PREDEFINED_TRIPS = [
  { pointOfLoading: 'EGCAI', voyageNumber: '3T117' },
  { pointOfLoading: 'SAJED', voyageNumber: '3T203' },
  { pointOfLoading: 'UGEBB', voyageNumber: '3T7386' },
  { pointOfLoading: 'SSJUB', voyageNumber: '3T7384' },
  { pointOfLoading: 'SARUH', voyageNumber: '3T217' },
  { pointOfLoading: 'SADMM', voyageNumber: '3T209' },
  { pointOfLoading: 'OMMCT', voyageNumber: '3T263' },
  { pointOfLoading: 'QADOH', voyageNumber: '3T235' },
  { pointOfLoading: 'KEKWI', voyageNumber: '3T231' },
  { pointOfLoading: 'ERASM', voyageNumber: '3T214' },
  { pointOfLoading: 'KENBO', voyageNumber: 'V8601' },
  { pointOfLoading: 'ETADD', voyageNumber: 'ET348' },
  { pointOfLoading: 'TRIST', voyageNumber: 'TK510' },
];
// Small field component with label (outside ManifestScreen)
function Field({ label, children, error }) {
  return (
    <div className={`field ${error ? "has-error" : ""}`}>
      <label>{label}</label>
      {children}
    </div>
  );
}

// Escape special characters for valid XML
function escapeXml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// Helper to get text from XML node
function getText(node, selector) {
  if (!node) return "";
  const el = node.querySelector(selector);
  return el ? el.textContent : "";
}

// Helper to format date for input[type="date"]
function toDateInput(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) {
    return String(v).slice(0, 10);
  }
  return d.toISOString().slice(0, 10);
}

// Default values for fixed general data
const DEFAULT_GENERAL = {
  // ✅ حقول ثابتة يدخلها المستخدم يدوياً وتُحفظ في manifest_header
  ownerOperator: "",
  registration: "00",
  flightNo: "",
  customsOfficeCode: "PZUA0",
  modeOfTransportCode: "4",
  transporterNationality: "SDN",
  placeOfDepartureCode: "",
  placeOfDestinationCode: "SDPZU",
};

// Flatten masters + homes into a single list
function flattenBols(masters = []) {
  const out = [];
  (masters || []).forEach((m) => {
    out.push(m);
    const homes = Array.isArray(m.homes) ? m.homes : [];
    homes.forEach((h) => out.push(h));
  });
  return out;
}

// Calculate totals from BOLs list
// Consolidation masters (741, with houses) already carry the sum of their houses,
// so they are excluded — same rule as the server's totals (bol_type_code <> '741').
function computeTotalsFromBols(masters) {
  const all = flattenBols(masters).filter((b) => {
    const hasHomes = Array.isArray(b.homes) && b.homes.length > 0;
    return !hasHomes && String(b.bolTypeCode || "") !== "741";
  });
  let totalBols = all.length;
  let totalPackages = 0;
  let totalContainers = 0;
  let totalGrossMass = 0;

  all.forEach((b) => {
    totalPackages += Number(b.numberOfPackages) || 0;
    totalContainers += Number(b.numOfCtnForThisBol) || 0;
    totalGrossMass += Number(b.grossMass) || 0;
  });

  return {
    totalBols: String(totalBols),
    totalPackages: String(totalPackages),
    totalContainers: String(totalContainers),
    totalGrossMass: String(Number(totalGrossMass.toFixed(3))),
  };
}

// Convert manifest object → AWmds XML text
function manifestToAwmdsXml(manifest) {
  const g = manifest.general || {};
  const masters = manifest.bols || [];
  const bols = [];
 

  (masters || []).forEach((m) => {
    bols.push({ ...m, __isHouse: false, __masterRef: "" });
    const homes = Array.isArray(m.homes) ? m.homes : [];
    homes.forEach((h) => {
      bols.push({ ...h, __isHouse: true, __masterRef: m.bolReference || "" });
    });
  });

  let xml = `<?xml version='1.0' encoding='utf-8'?>`;
  let lineCounter = 1;
  xml += `\n<Awmds>\n`;
  xml += `  <General_segment>\n`;
  xml += `    <General_segment_id>\n`;
  xml += `      <Customs_office_code>${escapeXml(g.customsOfficeCode)}</Customs_office_code>\n`;
  xml += `      <Voyage_number>${escapeXml(g.voyageNumber)}</Voyage_number>\n`;
  xml += `      <Date_of_departure>${escapeXml(g.dateOfDeparture)}</Date_of_departure>\n`;
  xml += `      <Date_of_arrival>${escapeXml(g.dateOfArrival)}</Date_of_arrival>\n`;
  xml += `    </General_segment_id>\n`;
  xml += `    <Totals_segment>\n`;
  xml += `      <Total_number_of_bols>${escapeXml(g.totalBols)}</Total_number_of_bols>\n`;
  xml += `      <Total_number_of_packages>${escapeXml(g.totalPackages)}</Total_number_of_packages>\n`;
  xml += `      <Total_number_of_containers>${escapeXml(g.totalContainers)}</Total_number_of_containers>\n`;
  xml += `      <Total_gross_mass>${escapeXml(g.totalGrossMass)}</Total_gross_mass>\n`;
  xml += `    </Totals_segment>\n`;
  xml += `    <Transport_information>\n`;
  xml += `      <Carrier>\n`;
  xml += `        <Carrier_code>00123</Carrier_code>\n`;
  xml += `        <Carrier_name>${escapeXml(g.carrierName)}</Carrier_name>\n`;
  xml += `        <Carrier_address>${escapeXml(g.carrierAddress)}</Carrier_address>\n`;
  xml += `      </Carrier>\n`;
  xml += `      <Shipping_Agent>\n`;
  xml += `        <Shipping_Agent_code>${escapeXml(g.shippingAgentCode)}</Shipping_Agent_code>\n`;
  xml += `        <Shipping_Agent_name>${escapeXml(g.shippingAgentName)}</Shipping_Agent_name>\n`;
  xml += `      </Shipping_Agent>\n`;
  xml += `      <Mode_of_transport_code>${escapeXml(g.modeOfTransportCode)}</Mode_of_transport_code>\n`;
  xml += `      <Identity_of_transporter>${escapeXml(g.identityOfTransporter)}</Identity_of_transporter>\n`;
  xml += `      <Nationality_of_transporter_code>${escapeXml(g.transporterNationality)}</Nationality_of_transporter_code>\n`;
  xml += `    </Transport_information>\n`;
  xml += `    <Load_unload_place>\n`;
  xml += `      <Place_of_departure_code>${escapeXml(g.placeOfDepartureCode)}</Place_of_departure_code>\n`;
  xml += `      <Place_of_destination_code>${escapeXml(g.placeOfDestinationCode)}</Place_of_destination_code>\n`;
  xml += `    </Load_unload_place>\n`;
  xml += `  </General_segment>\n`;

  bols.forEach((bol) => {
    xml += `  <Bol_segment>\n`;
    xml += `    <Bol_id>\n`;
    xml += `      <Bol_reference>${escapeXml(bol.bolReference)}</Bol_reference>\n`;
    xml += `      <Line_number>${lineCounter++}</Line_number>\n`;
    xml += `      <Bol_nature>${escapeXml(bol.bolNature)}</Bol_nature>\n`;
    xml += `      <Bol_type_code>${escapeXml(bol.bolTypeCode)}</Bol_type_code>\n`;
    if (bol.__isHouse) {
      xml += `      <Master_bol_ref_number>${escapeXml(bol.__masterRef || bol.masterBolRefNumber)}</Master_bol_ref_number>\n`;
    }
    xml += `    </Bol_id>\n`;
    xml += `    <Load_unload_place>\n`;
    xml += `      <Place_of_loading_code>${escapeXml(bol.placeOfLoadingCode || g.placeOfDepartureCode)}</Place_of_loading_code>\n`;
    xml += `      <Place_of_unloading_code>${escapeXml(bol.placeOfUnloadingCode || g.placeOfDestinationCode)}</Place_of_unloading_code>\n`;
    xml += `    </Load_unload_place>\n`;
    xml += `    <Traders_segment>\n`;
    xml += `      <Exporter>\n`;
    xml += `        <Exporter_name>${escapeXml(bol.exporterName)}</Exporter_name>\n`;
    xml += `        <Exporter_address>${escapeXml(bol.exporterAddress)}</Exporter_address>\n`;
    xml += `      </Exporter>\n`;
    xml += `      <Notify>\n`;
    xml += `        <Notify_name>${escapeXml(bol.notifyName)}</Notify_name>\n`;
    xml += `        <Notify_address>${escapeXml(bol.notifyAddress)}</Notify_address>\n`;
    xml += `      </Notify>\n`;
    xml += `      <Consignee>\n`;
    xml += `        <Consignee_name>${escapeXml(bol.consigneeName)}</Consignee_name>\n`;
    xml += `        <Consignee_address>${escapeXml(bol.consigneeAddress)}</Consignee_address>\n`;
    xml += `      </Consignee>\n`;
    xml += `    </Traders_segment>\n`;
    xml += `    <Goods_segment>\n`;
    xml += `      <Number_of_packages>${escapeXml(bol.numberOfPackages)}</Number_of_packages>\n`;
    xml += `      <Package_type_code>${escapeXml(bol.packageTypeCode)}</Package_type_code>\n`;
    xml += `      <Gross_mass>${escapeXml(bol.grossMass)}</Gross_mass>\n`;
    xml += `      <Shipping_marks>${escapeXml(bol.shippingMarks)}</Shipping_marks>\n`;
    xml += `      <Goods_description>${escapeXml(bol.goodsDescription)}</Goods_description>\n`;
    xml += `      <Num_of_ctn_for_this_bol>${escapeXml(bol.numOfCtnForThisBol)}</Num_of_ctn_for_this_bol>\n`;
    xml += `    </Goods_segment>\n`;
    xml += `    <Value_segment></Value_segment>\n`;
    xml += `  </Bol_segment>\n`;
  });

  xml += `</Awmds>\n`;
  return xml;
}

// Download XML text as file
function downloadXmlFile(xmlText, fileName = "manifest.xml") {
  const blob = new Blob([xmlText], { type: "application/xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

function makeTempId() {
  return `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

// ✅ Helper to save to localStorage (sqflit equivalent in web)
function saveLocalPair(key, name, address) {
  if (!name || !address) return;
  try {
    const raw = localStorage.getItem(key);
    let list = raw ? JSON.parse(raw) : [];
    const idx = list.findIndex((i) => i.name === name);
    if (idx > -1) {
      list[idx].address = address;
    } else {
      list.push({ name, address });
    }
    localStorage.setItem(key, JSON.stringify(list));
  } catch (e) {
    console.error("Error saving local pair", e);
  }
}

// ✅ Helper to save single values to localStorage
function saveLocalValue(key, value) {
  if (!value || !String(value).trim()) return;
  try {
    const raw = localStorage.getItem(key);
    let list = raw ? JSON.parse(raw) : [];
    const val = String(value).trim();
    const exists = list.some(item => item.name === val);
    if (!exists) {
      list.push({ name: val, address: "" }); // Keep structure for LocalAutocomplete
      localStorage.setItem(key, JSON.stringify(list));
    }
  } catch (e) {
    console.error(`Error saving local value for key ${key}:`, e);
  }
}

// ✅ Local Autocomplete Component
function LocalAutocomplete({ label, value, onChange, onSelect, storageKey, disabled, error }) {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);

  const handleSearch = (query) => {
    if (!query) { setSuggestions([]); return; }
    try {
      const raw = localStorage.getItem(storageKey);
      const list = raw ? JSON.parse(raw) : [];
      const filtered = list.filter((item) => (item.name || "").toLowerCase().includes(query.toLowerCase()));
      setSuggestions(filtered.slice(0, 10));
    } catch (e) { setSuggestions([]); }
  };

  return (
    <div className={`field ${error ? "has-error" : ""}`} style={{ position: "relative" }}>
      <label>{label}</label>

     {/* NEW: Apply conditional style if there is no any value */}




      <input
        value={value || ""}
        disabled={disabled}
        onChange={(e) => { const v = e.target.value; onChange(v); handleSearch(v); setShow(true); }}
        onFocus={() => { handleSearch(value || ""); setShow(true); }}
        onBlur={() => setTimeout(() => setShow(false), 200)}
      />
      {show && suggestions.length > 0 && (
        <ul className="dropdown-list">
          {suggestions.map((s, i) => (
            <li key={i} onMouseDown={() => { onSelect(s); setShow(false); }}>
              <strong>{s.name}</strong>
              <div className="hint">{s.address}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ManifestScreen({ onBack }) {
  const [mode, setMode] = useState("xml");
  const [xmlFile, setXmlFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [manifestQuery, setManifestQuery] = useState("");
  const [manifestList, setManifestList] = useState([]);
  const [filteredManifests, setFilteredManifests] = useState([]);
  const [showManifestDropdown, setShowManifestDropdown] = useState(false);
  const [selectedManifestId, setSelectedManifestId] = useState(null);
const [showGeneral, setShowGeneral] = useState(true);
const [voyageQuery, setVoyageQuery] = useState("");
const [voyageOptions, setVoyageOptions] = useState([]);
const [showVoyageDropdown, setShowVoyageDropdown] = useState(false);

// Owner autocomplete
const [ownerQuery, setOwnerQuery] = useState("");
const [ownerOptions, setOwnerOptions] = useState([]);
const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

  // ✅ BOL Status Check State
  const [bolStatusQuery, setBolStatusQuery] = useState("");
  const [bolStatusResult, setBolStatusResult] = useState(null);
  const [bolStatusLoading, setBolStatusLoading] = useState(false);

  const [focusedBol, setFocusedBol] = useState(null);
  const ignoreScrollRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (focusedBol && !ignoreScrollRef.current) {
        setFocusedBol(null);
      }
    };
    const handleClickOutside = (e) => {
      if (focusedBol && !e.target.closest(`#${focusedBol}`)) {
        setFocusedBol(null);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focusedBol]);

  const checkBolStatus = async () => {
    if (!bolStatusQuery.trim()) return;
    setBolStatusLoading(true);
    setBolStatusResult(null);
    try {
      const res = await api.get(`/bols/by-reference/${encodeURIComponent(bolStatusQuery.trim())}`);
      setBolStatusResult(res);
    } catch (err) {
      console.error(err);
      setBolStatusResult({ error: "AWB not found or error" });
    } finally {
      setBolStatusLoading(false);
    }
  };

const [voyageDraft, setVoyageDraft] = useState({
  voyageNumber: "",
  ownerOperator: "",
  customsOfficeCode: ""
});

  useEffect(() => {
    if (!showVoyageDropdown) return;
    const t = setTimeout(async () => {
      try {
        const q = (voyageQuery || "").trim();
        const url = q ? `/voyages?q=${encodeURIComponent(q)}` : `/voyages`;
        const rows = await api.get(url);
        setVoyageOptions(rows || []);
      } catch (err) {
        console.error("Error fetching voyages:", err);
        setVoyageOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [voyageQuery, showVoyageDropdown]);

  // Services list for per-BOL selection
  const [servicesList, setServicesList] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const s = await api.get("/services");
        setServicesList(Array.isArray(s) ? s : []);
      } catch (err) {
        console.error("Error loading services:", err);
        setServicesList([]);
      }
    };
    fetch();
  }, []);


useEffect(() => {
  if (!voyageDraft.voyageNumber?.trim()) return;

  // لا تحفظ إذا كل الحقول الأخرى فاضية
  if (
    !voyageDraft.ownerOperator?.trim() &&
    !voyageDraft.customsOfficeCode?.trim()
  ) {
    return;
  }

  const t = setTimeout(async () => {
    try {
      await api.post("/voyages", {
        voyageNumber: voyageDraft.voyageNumber.trim(),
        ownerOperator: voyageDraft.ownerOperator?.trim() || null,
        customsOfficeCode: voyageDraft.customsOfficeCode?.trim() || null
      });
    } catch (e) {
      console.error("Auto save voyage failed", e);
    }
  }, 600);

  return () => clearTimeout(t);
}, [voyageDraft]);


useEffect(() => {
  if (!showOwnerDropdown) return;

  const t = setTimeout(async () => {
    try {
      const q = (ownerQuery || "").trim();
      const url = q ? `/owners?q=${encodeURIComponent(q)}` : `/owners`;
      const rows = await api.get(url);
      setOwnerOptions(rows || []);
      setShowOwnerDropdown(true);
    } catch (err) {
      console.error("Error fetching owners:", err);
      setOwnerOptions([]);
    }
  }, 250);

  return () => clearTimeout(t);
}, [ownerQuery, showOwnerDropdown]);







  const [manifest, setManifest] = useState({
    general: {
      customsOfficeCode: DEFAULT_GENERAL.customsOfficeCode,
      declaredTotalBols: "",
      voyageNumber: "",
      dateOfDeparture: "",
      dateOfArrival: "",
      totalBols: "0",
      totalPackages: "0",
      totalContainers: "0",
      totalGrossMass: "0",
      modeOfTransportCode: DEFAULT_GENERAL.modeOfTransportCode,
      transporterNationality: DEFAULT_GENERAL.transporterNationality,
      placeOfDepartureCode: DEFAULT_GENERAL.placeOfDepartureCode,
      placeOfDestinationCode: DEFAULT_GENERAL.placeOfDestinationCode,
      carrierCode: "",
      carrierName: "",
      carrierAddress: "",
      shippingAgentCode: "",
      shippingAgentName: "",
      identityOfTransporter: "",
    },
    bols: [],
  });

  const { ownerOperator, flightNo, customsOfficeCode, voyageNumber } = manifest.general;

  const permissions = readPerms();
  const canSearchManifest = has(permissions, "MANIFEST_SEARCH") || has(permissions, "MANIFEST_EDIT");
  const canCreateManifest = has(permissions, "MANIFEST_CREATE") || has(permissions, "MANIFEST_ENTRY");
  const canEditManifest = has(permissions, "MANIFEST_EDIT") || has(permissions, "SERVICES_EDIT");

  const loadManifestList = async (force = false) => {
    if (!canSearchManifest) return;
    if (!force && manifestList.length > 0) return;
    try {
      const res = await api.get("/manifests/list");
      setManifestList(res);
      setFilteredManifests(res);
    } catch (err) {
      console.error(err);
    }
  };

  const handleManifestInputChange = (value) => {
    setManifestQuery(value);
    const v = value.trim().toLowerCase();
    if (!v) {
      setFilteredManifests(manifestList);
      return;
    }
    const filtered = manifestList.filter((m) => {
      const idStr = String(m.id || "").toLowerCase();
      const voyage = String(m.voyage_number || "").toLowerCase();
      const arrival = String(m.date_of_arrival || "").toLowerCase();
      return idStr.includes(v) || voyage.includes(v) || arrival.includes(v);
    });
    setFilteredManifests(filtered);
  };

  const mapBolRowToUi = (b) => ({
    id: b.id,
    tempId: b.id ? null : makeTempId(),
    isLocked: Number(b.is_locked) === 1,
    day: Number(b.day) === 1,
    bolReference: b.bol_reference || "",
    lineNumber: b.line_number || "",
    bolNature: b.bol_nature || "",
    bolTypeCode: b.bol_type_code || "",
    placeOfLoadingCode: b.place_of_loading_code || "",
    placeOfUnloadingCode: b.place_of_unloading_code || "",
    exporterName: b.exporter_name || "",
    exporterAddress: b.exporter_address || "",
    consigneeName: b.consignee_name || "",
    consigneeAddress: b.consignee_address || "",
    notifyName: b.notify_name || "",
    notifyAddress: b.notify_address || "",
    numberOfPackages: b.number_of_packages?.toString() || "",
    packageTypeCode: b.package_type_code || "",
    grossMass: b.gross_mass?.toString() || "",
    shippingMarks: b.shipping_marks || "",
    goodsDescription: b.goods_description || "",
    numOfCtnForThisBol: b.num_of_ctn_for_this_bol?.toString() || "",
    serviceId: b.service_id != null ? b.service_id : null,
    homes: [],
  });

  const handleSelectManifest = async (m) => {
    try {
      setLoading(true);
      setShowManifestDropdown(false);
      setManifestQuery(String(m.id));
      setSelectedManifestId(m.id);

      // ✅ تم تعديل المسار ليتوافق مع السيرفر (إضافة /full)
      const res = await api.get(`/manifests/${m.id}/full`);
      const { header, bols } = res;

      const general = {
        ownerOperator: header.owner_operator || "",
        registration: header.registration || "",
        flightNo: header.flight_no || "",
        customsOfficeCode: header.customs_office_code || "",
        declaredTotalBols: header.declared_total_bols != null ? String(header.declared_total_bols) : "",
        voyageNumber: header.voyage_number || "",
        dateOfDeparture: toDateInput(header.date_of_departure),
        dateOfArrival: toDateInput(header.date_of_arrival),
        totalBols: (header.total_bols ?? (bols || []).length)?.toString() || "",
        totalPackages: (header.total_packages ?? "")?.toString() || "",
        totalContainers: (header.total_containers ?? "")?.toString() || "",
        totalGrossMass: (header.total_gross_mass ?? "")?.toString() || "",
        modeOfTransportCode: header.mode_of_transport_code || "",
        transporterNationality: header.transporter_nationality || "",
        placeOfDepartureCode: header.place_of_departure_code || "",
        placeOfDestinationCode: header.place_of_destination_code || "",
        carrierCode: header.carrier_code || "",
        carrierName: header.carrier_name || "",
        carrierAddress: header.carrier_address || "",
        shippingAgentCode: header.shipping_agent_code || "",
        shippingAgentName: header.shipping_agent_name || "",
        identityOfTransporter: header.identity_of_transporter || "",
      };

      const mappedMasters = (bols || []).map((masterRow) => {
        const master = mapBolRowToUi(masterRow);
        const homes = Array.isArray(masterRow.homes) ? masterRow.homes : [];
        master.homes = homes.map((h) => mapBolRowToUi(h));
        return master;
      });

      const totals = computeTotalsFromBols(mappedMasters);
      setManifest({ general: { ...general, ...totals }, bols: mappedMasters });
      setMode("manual");
      setMessage({ text: "Manifest loaded successfully ✅", type: "success" });
    } catch (err) {
      console.error(err);
      setMessage({ text: err?.message || "Error loading manifest details", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const parseAwmdsXml = (xmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "application/xml");
    const genId = doc.querySelector("General_segment_id");
    const totalsSeg = doc.querySelector("Totals_segment");
    const transInfo = doc.querySelector("Transport_information");
    const loadPlace = doc.querySelector("Load_unload_place");

    const general = {
      customsOfficeCode: getText(genId, "Customs_office_code"),
      voyageNumber: getText(genId, "Voyage_number"),
      dateOfDeparture: getText(genId, "Date_of_departure"),
      dateOfArrival: getText(genId, "Date_of_arrival"),
      totalBols: getText(totalsSeg, "Total_number_of_bols"),
      totalPackages: getText(totalsSeg, "Total_number_of_packages"),
      totalContainers: getText(totalsSeg, "Total_number_of_containers"),
      totalGrossMass: getText(totalsSeg, "Total_gross_mass"),
      carrierCode: getText(transInfo?.querySelector("Carrier"), "Carrier_code"),
      carrierName: getText(transInfo?.querySelector("Carrier"), "Carrier_name"),
      carrierAddress: getText(transInfo?.querySelector("Carrier"), "Carrier_address"),
      shippingAgentCode: getText(transInfo?.querySelector("Shipping_Agent"), "Shipping_Agent_code"),
      shippingAgentName: getText(transInfo?.querySelector("Shipping_Agent"), "Shipping_Agent_name"),
      modeOfTransportCode: getText(transInfo, "Mode_of_transport_code"),
      identityOfTransporter: getText(transInfo, "Identity_of_transporter"),
      transporterNationality: getText(transInfo, "Nationality_of_transporter_code"),
      placeOfDepartureCode: getText(loadPlace, "Place_of_departure_code"),
      placeOfDestinationCode: getText(loadPlace, "Place_of_destination_code"),
    };

    const bolNodes = Array.from(doc.querySelectorAll("Bol_segment"));
    const masters = [];
    const houses = [];

    bolNodes.forEach((bolNode) => {
      const bolId = bolNode.querySelector("Bol_id");
      const bolTypeCode = getText(bolId, "Bol_type_code");
      const bolObj = {
        id: null,
        tempId: makeTempId(),
        isLocked: false,
        bolReference: getText(bolId, "Bol_reference"),
        lineNumber: getText(bolId, "Line_number"),
        bolNature: getText(bolId, "Bol_nature"),
        masterBolRefNumber: getText(bolId, "Master_bol_ref_number"),
        bolTypeCode,
        placeOfLoadingCode: getText(bolNode.querySelector("Load_unload_place"), "Place_of_loading_code"),
        placeOfUnloadingCode: getText(bolNode.querySelector("Load_unload_place"), "Place_of_unloading_code"),
        exporterName: getText(bolNode.querySelector("Exporter"), "Exporter_name"),
        exporterAddress: getText(bolNode.querySelector("Exporter"), "Exporter_address"),
        consigneeName: getText(bolNode.querySelector("Consignee"), "Consignee_name"),
        consigneeAddress: getText(bolNode.querySelector("Consignee"), "Consignee_address"),
        notifyName: getText(bolNode.querySelector("Notify"), "Notify_name"),
        notifyAddress: getText(bolNode.querySelector("Notify"), "Notify_address"),
        numberOfPackages: getText(bolNode.querySelector("Goods_segment"), "Number_of_packages"),
        packageTypeCode: getText(bolNode.querySelector("Goods_segment"), "Package_type_code"),
        grossMass: getText(bolNode.querySelector("Goods_segment"), "Gross_mass"),
        shippingMarks: getText(bolNode.querySelector("Goods_segment"), "Shipping_marks"),
        goodsDescription: getText(bolNode.querySelector("Goods_segment"), "Goods_description"),
        numOfCtnForThisBol: getText(bolNode.querySelector("Goods_segment"), "Num_of_ctn_for_this_bol"),
        homes: [],
      };

      if (bolTypeCode === "H" || bolObj.masterBolRefNumber) {
        houses.push(bolObj);
      } else {
        masters.push(bolObj);
      }
    });

    houses.forEach((house) => {
      const masterRef = house.masterBolRefNumber || house.bolNature;
      const master = masters.find((m) => m.bolReference === masterRef);
      if (master) {
        master.homes.push(house);
      } else {
        masters.push(house);
      }
    });

    const totals = computeTotalsFromBols(masters);
    return { general: { ...general, ...totals }, bols: masters };
  };

  const handleFileChange = (e) => {
    setXmlFile(e.target.files[0] || null);
    setMessage({ text: "", type: "" });
  };

  const handleLoadFromXml = () => {
    if (!xmlFile) {
      setMessage({ text: "Please select an XML file first", type: "error" });
      return;
    }
    resetForm();
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let xmlText = e.target.result;
        xmlText = xmlText.replaceAll("SDPZU", "SDPZU");
        const parsed = parseAwmdsXml(xmlText);
        setSelectedManifestId(null);
        setManifestQuery("");
        setShowManifestDropdown(false);
        setManifest(parsed);
        setMode("manual");
        setMessage({ text: "XML data loaded successfully ✅", type: "success" });
      } catch (err) {
        console.error(err);
        setMessage({ text: "Error while reading XML file", type: "error" });
      }
    };
    reader.readAsText(xmlFile);
  };

  const handleGeneralChange = (field, value) => {
    setManifest((prev) => ({
      ...prev,
      general: { ...prev.general, [field]: value },
    }));
  };

  const handlePlaceOfDepartureChange = (value) => {
    if (value.length > 5) {
      setMessage({ text: "Warning: Point of loading cannot exceed 5 characters", type: "error" });
      return;
    }
    handleGeneralChange("placeOfDepartureCode", value);

    const trip = PREDEFINED_TRIPS.find(t => t.pointOfLoading.toUpperCase() === value.toUpperCase());

    if (trip) {
      handleGeneralChange("voyageNumber", trip.voyageNumber);
      handleGeneralChange("flightNo", trip.voyageNumber);
    }
  };

const handleBolChange = (masterIndex, field, value, homeIndex = null) => {
  setManifest(prev => {
    const masters = [...prev.bols];
    const master = masters[masterIndex];
    if (!master) return prev;

    const isDistributable = field === "grossMass" || field === "numberOfPackages";

    // ======================
    // MASTER → HOUSES
    // ======================
    if (homeIndex === null) {
      if (master.isLocked) return prev;

      const updatedMaster = { ...master, [field]: value };

      if (isDistributable && Array.isArray(master.homes) && master.homes.length > 0) {
        const perHouse = Number(value || 0) / master.homes.length;

        updatedMaster.homes = master.homes.map(h => ({
          ...h,
          [field]: Number.isFinite(perHouse)
            ? perHouse.toFixed(2)
            : ""
        }));
      }

      masters[masterIndex] = updatedMaster;
    }

    // ======================
    // HOUSES → MASTER
    // ======================
    if (homeIndex !== null) {
      const homes = [...(master.homes || [])];
      const home = homes[homeIndex];
      if (!home || home.isLocked) return prev;

      homes[homeIndex] = { ...home, [field]: value };

      let updatedMaster = { ...master, homes };

      if (isDistributable) {
        const sum = homes.reduce(
          (acc, h) => acc + Number(h[field] || 0),
          0
        );
        updatedMaster[field] = sum.toFixed(2);
      }

      masters[masterIndex] = updatedMaster;
    }

    // ======================
    // AWB TYPE CODE (AUTO)
    // ======================
    masters.forEach(m => {
      if (!m.homes || m.homes.length === 0) {
        m.bolTypeCode = "740";
      } else {
        m.bolTypeCode = "741";
        m.homes.forEach(h => {
          h.bolTypeCode = "740";
        });
      }
    });

    const totals = computeTotalsFromBols(masters);
    return {
      ...prev,
      bols: masters,
      general: { ...prev.general, ...totals }
    };
  });
};


  const handleAddBol = () => {
    setManifest((prev) => {
      const newMaster = {
        id: null,
        tempId: makeTempId(),
        isLocked: false,
        bolReference: "",
        lineNumber: "",
        bolNature: "23",
        bolTypeCode: "740",
        packageTypeCode: "PK",
        placeOfLoadingCode: "",
        placeOfUnloadingCode: "",
        exporterName: "",
        exporterAddress: "",
        consigneeName: "",
        consigneeAddress: "",
        notifyName: "PORTSUDAN SUDAN",
        notifyAddress: "PORTSUDAN SUDAN",
        numberOfPackages: "",
        grossMass: "",
        shippingMarks: "A",
        goodsDescription: "",
        numOfCtnForThisBol: "0", // مخفي
        day: false,
        homes: [],
      };
      const newBols = [...prev.bols, newMaster];
      const totals = computeTotalsFromBols(newBols);
      return { ...prev, bols: newBols, general: { ...prev.general, ...totals } };
    });
  };

  const handleAddHome = (masterIndex) => {
    setManifest((prev) => {
      const masters = [...prev.bols];
      const master = masters[masterIndex];
      if (!master || master.isLocked) return prev;
      const homes = Array.isArray(master.homes) ? [...master.homes] : [];
      const newHome = {
        id: null,
        tempId: makeTempId(),
        isLocked: false,
        bolReference: "",
        lineNumber: "",
        bolNature: "23",
        bolTypeCode: "740",
        packageTypeCode: "PK",
        placeOfLoadingCode: "",
        placeOfUnloadingCode: "",
        exporterName: "",
        exporterAddress: "",
        consigneeName: "",
        consigneeAddress: "",
        notifyName: "PORTSUDAN SUDAN",
        notifyAddress: "PORTSUDAN SUDAN",
        numberOfPackages: "",
        grossMass: "",
        shippingMarks: "A",
        goodsDescription: "",
        numOfCtnForThisBol: "0", // مخفي
        day: false,
      };
      masters[masterIndex] = { ...master, homes: [...homes, newHome], bolTypeCode: "741" };
      const totals = computeTotalsFromBols(masters);
      return { ...prev, bols: masters, general: { ...prev.general, ...totals } };
    });
  };

  // ✅ Delete Master BOL
  const handleDeleteBol = (index) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه البوليصة (Master AWB)؟")) return;
    setManifest((prev) => {
      const newBols = [...prev.bols];
      newBols.splice(index, 1);
      const totals = computeTotalsFromBols(newBols);
      return { ...prev, bols: newBols, general: { ...prev.general, ...totals } };
    });
  };

  // ✅ Delete House BOL
  const handleDeleteHome = (masterIndex, homeIndex) => {
    if (!window.confirm("هل أنت متأكد من حذف بوليصة الهاوس (House AWB)؟")) return;
    setManifest((prev) => {
      const masters = [...prev.bols];
      const master = { ...masters[masterIndex] };
      const homes = [...(master.homes || [])];
      homes.splice(homeIndex, 1);
      master.homes = homes;
      if (homes.length === 0) master.bolTypeCode = "740"; // Revert to direct AWB if no houses
      masters[masterIndex] = master;
      const totals = computeTotalsFromBols(masters);
      return { ...prev, bols: masters, general: { ...prev.general, ...totals } };
    });
  };

  const validateManifest = () => {
    const g = manifest.general;
    if (!g.customsOfficeCode || !g.voyageNumber || !g.dateOfDeparture || !g.dateOfArrival) {
      return "تنبيه: يرجى ملء جميع الحقول العامة المطلوبة والمحددة باللون الأحمر.";
    }
    const all = flattenBols(manifest.bols);
    if (all.length === 0) return "تنبيه: يجب إضافة بوليصة شحن واحدة على الأقل.";
    for (let i = 0; i < all.length; i++) {
      const b = all[i];
      // ✅ الماستر التجميعي (يحتوي هاوسات، NAWB type 741) لا يُستخدم فعلياً في التسليم/الفوترة/المخزون
      // لذلك لا تُطلب منه خدمة — الخدمة مطلوبة فقط للماستر المستقل (بدون هاوسات) ولكل هاوس
      const isConsolidationMaster = Array.isArray(b.homes) && b.homes.length > 0;
      if (
        !b.bolReference ||
        !b.bolNature ||
        !b.bolTypeCode ||
        !b.exporterName ||
        !b.exporterAddress ||
        !b.consigneeName ||
        !b.consigneeAddress ||
        !b.notifyName ||
        !b.notifyAddress ||
        !b.numberOfPackages ||
        !b.packageTypeCode ||
        !b.grossMass ||
        !b.shippingMarks ||
        !b.goodsDescription ||
        (!isConsolidationMaster && !b.serviceId)
      ) {
        return `تنبيه: يرجى ملء الحقول المطلوبة${isConsolidationMaster ? "" : " (بما في ذلك الخدمة)"} في البوليصة رقم #${i + 1}`;
      }
    }
    return null;
  };

  const handleExportXml = () => {
    const xmlText = manifestToAwmdsXml(manifest);
    const fileName = `MANIFEST_${manifest.general.voyageNumber || "NEW"}_${manifest.general.dateOfArrival || ""}.xml`;
    downloadXmlFile(xmlText, fileName);
  };

  const handleSave = async (silent = false) => {
    if (selectedManifestId && !canEditManifest) {
      setMessage({ text: "ليس لديك صلاحية تعديل Manifest", type: "error" });
      return false;
    }
    if (!selectedManifestId && !canCreateManifest) {
      setMessage({ text: "ليس لديك صلاحية إنشاء Manifest", type: "error" });
      return false;
    }

    // ✅ منع تكرار رقم البوليصة
    const allBols = flattenBols(manifest.bols);
    const refs = allBols.map(b => String(b.bolReference).trim().toUpperCase());
    if (new Set(refs).size !== refs.length) {
      setMessage({
        text: "تنبيه: رقم بوليصة الشحن (AWB Reference) مُكرَّر. يرجى التأكد من عدم وجود تكرار.",
        type: "error",
      });
      return false;
    }

    // ===
    // التحقق من البيانات
    // ===


    const errMsg = validateManifest();
    if (errMsg) {
      setShowErrors(true);
      setMessage({ text: errMsg, type: "error" });
      return false;
    }
    setShowErrors(false);
    try {
      setLoading(true);
      setMessage({ text: "", type: "" });
      const editableMasters = (manifest.bols || [])
        .map((m) => {
          // If the master is hidden or has the success icon, it's already been saved or is processing. Skip it.
          if (m.hidden || m.showSuccessIcon) return null;
          if (m.isLocked && (!m.homes || m.homes.length === 0)) return null;

          const homes = Array.isArray(m.homes) ? m.homes : [];
          // Also filter out hidden or success-marked homes
          const editableHomes = homes.filter((h) => (!h.isLocked || !h.id) && !h.hidden && !h.showSuccessIcon);

          if (!(!m.isLocked || !m.id) && editableHomes.length === 0) return null;

          const cleanedMaster = { ...m, homes: editableHomes };
          delete cleanedMaster.tempId;
          cleanedMaster.homes = cleanedMaster.homes.map((h) => {
            const hh = { ...h };
            delete hh.tempId;
            return hh;
          });
          return cleanedMaster;
        })
        .filter(Boolean);

      const payload = { general: manifest.general, bols: editableMasters };
      if (selectedManifestId) {
        await api.put(`/manifests/${selectedManifestId}`, payload);
      } else {
        await api.post("/manifest", payload);
      }

      // Save owner profile on successful manifest save
      const { ownerOperator, flightNo, customsOfficeCode, voyageNumber } = manifest.general;
      if (ownerOperator?.trim()) {
        if (flightNo?.trim() || customsOfficeCode?.trim() || voyageNumber?.trim()) {
          try {
            await api.post("/owners", {
              ownerOperator: ownerOperator.trim(),
              flightNo: flightNo?.trim() || null,
              customsOfficeCode: customsOfficeCode?.trim() || null,
              voyageNumber: voyageNumber?.trim() || null,
            });
          } catch (ownerErr) {
            console.error("Could not save owner profile:", ownerErr);
            // Non-critical error, just log it.
          }
        }
      }

      // ✅ Save Shippers & Consignees locally (sqflit style)
      manifest.bols.forEach(b => {
        saveLocalPair("shippers", b.exporterName, b.exporterAddress);
        saveLocalPair("consignees", b.consigneeName, b.consigneeAddress);
        saveLocalValue("goods_descriptions", b.goodsDescription);
        (b.homes || []).forEach(h => {
          saveLocalPair("shippers", h.exporterName, h.exporterAddress);
          saveLocalPair("consignees", h.consigneeName, h.consigneeAddress);
          saveLocalValue("goods_descriptions", h.goodsDescription);
        });
      });

      if (!silent) {
        setMessage({ text: "Saved successfully ✅", type: "success" });
      }
      await loadManifestList(true);
      return true;
    } catch (err) {
      setMessage({ text: err.message || "Server error", type: "error" });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedManifestId(null);
    setManifestQuery("");
    setManifest({
      general: {
        customsOfficeCode: DEFAULT_GENERAL.customsOfficeCode,
        voyageNumber: "",
        dateOfDeparture: "",
        dateOfArrival: "",
        totalBols: "0",
        totalPackages: "0",
        totalContainers: "0",
        totalGrossMass: "0",
        modeOfTransportCode: DEFAULT_GENERAL.modeOfTransportCode,
        transporterNationality: DEFAULT_GENERAL.transporterNationality,
        placeOfDepartureCode: DEFAULT_GENERAL.placeOfDepartureCode,
        placeOfDestinationCode: DEFAULT_GENERAL.placeOfDestinationCode,
        carrierCode: "",
        carrierName: "",
        carrierAddress: "",
        shippingAgentCode: "",
        shippingAgentName: "",
        identityOfTransporter: "",
      },
      bols: [],
    });
    setXmlFile(null);
    setMode("manual");
  };

  return (
    <div className="screen screen-rtl">
      <style>{`
        .has-error input, 
        .has-error select, 
        .has-error .select-field select {
          border: 2px solid #ff4d4f !important;
        }
        .has-error label {
          color: #ff4d4f;
        }
      `}</style>
      <div className="screen-header">
        <h2>Manifest Data Entry (AWMDS)</h2>
      </div>

      {/* ✅ Check AWB Status Section */}
      <div className="card">
        <h3>Check AWB Status</h3>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          <div className="field" style={{ flex: 1 }}>
            <label>AWB Reference</label>
            <input
              value={bolStatusQuery}
              onChange={(e) => setBolStatusQuery(e.target.value)}
              placeholder="Enter AWB Ref"
              onKeyDown={(e) => e.key === "Enter" && checkBolStatus()}
            />
          </div>
          <button type="button" onClick={checkBolStatus} disabled={bolStatusLoading}>
            {bolStatusLoading ? "Checking..." : "Check"}
          </button>
        </div>

        {bolStatusResult && (
          <div style={{ marginTop: "1rem" }}>
            {bolStatusResult.error ? (
              <div className="message error">{bolStatusResult.error}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {(Array.isArray(bolStatusResult) ? bolStatusResult : []).map((bol, idx) => {
                  const isDirectService = String(bol.service_name || "").toLowerCase().includes("direct");
                  let statusText = "";
                  let statusBg = "";
                  let statusColor = "";

                  if (bol.is_delivered) {
                    statusText = "Delivered ✅";
                    statusBg = "#e8f5e9";
                    statusColor = "#2e7d32";
                  } else if (bol.is_do_issued) {
                    statusText = "Delivery Order Issued (Not Delivered) 📜";
                    statusBg = "#fff3e0";
                    statusColor = "#ef6c00";
                  } else {
                    statusText = "Pending (No DO Issued) ⏳";
                    statusBg = "#ffebee";
                    statusColor = "#c62828";
                  }

                  return (
                    <div key={bol.id || idx} style={{ padding: "1rem", borderRadius: "8px", backgroundColor: statusBg, color: statusColor, border: `1px solid ${statusColor}` }}>
                      <h4 style={{ margin: "0 0 0.5rem 0" }}>{statusText}</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.5rem", fontSize: "0.9rem", color: "#333" }}>
                        <div><strong>AWB:</strong> {bol.bol_reference}</div>
                        <div><strong>Consignee:</strong> {bol.consignee_name}</div>
                        <div><strong>Flight:</strong> {bol.flight_no || bol.voyage_number}</div>
                        <div><strong>Arrival:</strong> {bol.date_of_arrival ? String(bol.date_of_arrival).slice(0, 10) : ""}</div>
                        <div><strong>Pieces:</strong> {bol.number_of_packages}</div>
                        <div><strong>Weight:</strong> {bol.gross_mass}</div>
                        <div><strong>Registration:</strong> {bol.registration || ""}</div>
                        <div>
                          <strong>Delivery Date:</strong>{' '}
                          {isDirectService ? "direct" : (bol.delivery_date ? String(bol.delivery_date).slice(0, 10) : "-")}
                        </div>
                        <div><strong>DO Recipient:</strong> {bol.do_recipient || "-"}</div>
                        <div><strong>Recipient Phone:</strong> {bol.do_recipient_phone || "-"}</div>
                        <div><strong>DO Received Date:</strong> {bol.do_received_date ? String(bol.do_received_date).slice(0, 10) : "-"}</div>
                      </div>

                      {/* عرض حالة الهاوسات داخل الماستر */}
                      {bol.houses && bol.houses.length > 0 && (
                        <div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
                          <strong style={{ fontSize: '0.9rem', color: '#555' }}>Houses Status (بوالص الهاوس):</strong>
                          <ul style={{ fontSize: '0.85rem', marginTop: '5px', listStyle: 'none', padding: 0 }}>
                            {bol.houses.map(h => (
                              <li key={h.id} style={{ marginBottom: '4px', padding: '4px', backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                                📄 <strong>{h.bol_reference}</strong>: 
                                {h.is_delivered 
                                  ? <span style={{ color: '#2e7d32' }}> Delivered ✅ ({String(h.delivery_date).slice(0, 10)})</span> 
                                  : <span style={{ color: '#ef6c00' }}> Pending ⏳ (لم تسلم بعد)</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {canSearchManifest && (
        <div className="card">
          <h3>Search Manifest</h3>
          <div className="autocomplete">
            <input
              type="text"
              placeholder="Search by Manifest ID / Flight / Arrival Date"
              value={manifestQuery}
              onFocus={() => {
                loadManifestList();
                setShowManifestDropdown(true);
              }}
              onChange={(e) => handleManifestInputChange(e.target.value)}
            />
            {showManifestDropdown && (
              <ul className="dropdown-list">
                {filteredManifests.length === 0 && <li className="empty">No results</li>}
                {filteredManifests.map((m) => (
                  <li key={m.id} onClick={() => handleSelectManifest(m)}>
                    <strong>Manifest #{m.id}</strong>
                    <div className="hint">
                      Flight: {m.voyage_number || "—"} | Arrival: {m.date_of_arrival || "—"} | AWBs: {m.total_bols ?? "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="mode-switch">
        <button
          type="button"
          className={mode === "xml" ? "active-tab" : ""}
          onClick={() => {
            resetForm();
            setSelectedManifestId(null);
            setManifestQuery("");
            setShowManifestDropdown(false);
            setMode("xml");
          }}
        >
          Load from XML
        </button>
        <button
          type="button"
          className={mode === "manual" ? "active-tab" : ""}
            onClick={() => {
    resetForm();          // 👈 تفريغ كل الحقول
    setMode("manual");
  }}
        >
          Manual Entry
        </button>
      </div>

      {mode === "xml" && (
        <div className="xml-upload">
          <input type="file" accept=".xml" onChange={handleFileChange} />
          <button type="button" onClick={handleLoadFromXml}
          className={`interactive ${loading ? "loading" : ""}`}
          >
            Load data from file
          </button>
        </div>
      )}

 <div className="card">
  <div className="card-header-row">
    <h3>General Information</h3>
    <button
      type="button"
      className="secondary-btn"
      onClick={() => setShowGeneral(v => !v)}
    >
      {showGeneral ? "Hide ▲" : "Show ▼"}
    </button>
  </div>

  {showGeneral && (
    <div className="grid grid-auto">
<Field label="Owner or Operator" error={showErrors && !manifest.general.ownerOperator}>
  <input
    value={manifest.general.ownerOperator}
    onFocus={() => {
      setShowOwnerDropdown(true);
      // preload options
      setOwnerQuery(manifest.general.ownerOperator || "");
    }}
    onChange={(e) => {
      const v = e.target.value;
      handleGeneralChange("ownerOperator", v);
      setOwnerQuery(v);
    }}
  />

  {showOwnerDropdown && ownerOptions.length > 0 && (
    <ul className="dropdown-list">
      {ownerOptions.map((o, idx) => (
        <li
          key={o.owner_operator + "_" + idx}
          onMouseDown={() => {
            handleGeneralChange("ownerOperator", o.owner_operator || "");
            handleGeneralChange("customsOfficeCode", o.customs_office_code || "");
            setShowOwnerDropdown(false);
            setOwnerOptions([]);
          }}
        >
          <strong>{o.owner_operator}</strong>
          <div className="hint">{o.flight_no || "-"} | {o.customs_office_code || "-"} | {o.voyage_number || "-"}</div>
        </li>
      ))}
    </ul>
  )}
</Field>
          <Field label="Registration" error={showErrors && !manifest.general.registration}>
            <input
              value={manifest.general.registration}
              onChange={(e) => handleGeneralChange("registration", e.target.value)}
            />
          </Field>
                    <Field label="Declared Total AWBs" error={showErrors && !manifest.general.declaredTotalBols}>
                      <input
                        value={manifest.general.declaredTotalBols}
                        onChange={(e) => handleGeneralChange("declaredTotalBols", e.target.value)}
                      />
                    </Field>
 <Field label="Customs Office Code" error={showErrors && !manifest.general.customsOfficeCode}>
  <input
    value={manifest.general.customsOfficeCode}
onChange={(e) => {
  const v = e.target.value;
  handleGeneralChange("customsOfficeCode", v);
  setVoyageDraft(d => ({ ...d, customsOfficeCode: v }));
}}

  />
</Field>
<Field label="Voyage Number" error={showErrors && !manifest.general.voyageNumber}>
  <input
    value={manifest.general.voyageNumber}
    onChange={(e) => {
      const v = e.target.value;
      setVoyageQuery(v);
      handleGeneralChange("voyageNumber", v);
      setVoyageDraft(d => ({ ...d, voyageNumber: v }));
      setShowVoyageDropdown(true);
    }}
  />

  {showVoyageDropdown && voyageOptions.length > 0 && (
    <ul className="dropdown-list">
      {voyageOptions.map(v => (
        <li
          key={v.voyage_number}
          onMouseDown={() => {
            handleGeneralChange("voyageNumber", v.voyage_number);
            handleGeneralChange("ownerOperator", v.owner_operator || "");
            handleGeneralChange("customsOfficeCode", v.customs_office_code || "");
            setVoyageDraft({
              voyageNumber: v.voyage_number,
              ownerOperator: v.owner_operator || "",
              customsOfficeCode: v.customs_office_code || ""
            });
            setShowVoyageDropdown(false);
          }}
        >
          <strong>{v.voyage_number}</strong>
          <div className="hint">
            {v.owner_operator} | {v.customs_office_code}
          </div>
        </li>
      ))}
    </ul>
  )}
</Field>



          <Field label="Flight No." error={showErrors && !manifest.general.flightNo}>
            <input
              value={manifest.general.flightNo}
              onChange={(e) => handleGeneralChange("flightNo", e.target.value)}
            />
          </Field>
          <Field label="Date of Departure" error={showErrors && !manifest.general.dateOfDeparture}>
            <input
              type="date"
              value={manifest.general.dateOfDeparture}
              onChange={(e) => handleGeneralChange("dateOfDeparture", e.target.value)}
            />
          </Field>
          <Field label="Date of Arrival" error={showErrors && !manifest.general.dateOfArrival}>
            <input
              type="date"
              value={manifest.general.dateOfArrival}
              onChange={(e) => handleGeneralChange("dateOfArrival", e.target.value)}
            />
            </Field>

            {/*
          </Field>
          <Field label="Total Number of AWB">
            <input value={manifest.general.totalBols} readOnly />
          </Field>
          <Field label="Total Number of Packages">
            <input value={manifest.general.totalPackages} readOnly />
          </Field>
          <Field label="Total Number of Containers">
            <input value={manifest.general.totalContainers} readOnly />
          </Field>
          <Field label="Total Gross Mass">
            <input value={manifest.general.totalGrossMass} readOnly />
          </Field>
          */}
          <Field label="Mode of Transport Code" error={showErrors && !manifest.general.modeOfTransportCode}>
            <input
              value={manifest.general.modeOfTransportCode}
              onChange={(e) => handleGeneralChange("modeOfTransportCode", e.target.value)}
            />
          </Field>
          <Field label="Transporter Nationality" error={showErrors && !manifest.general.transporterNationality}>
            <input
              value={manifest.general.transporterNationality}
              onChange={(e) => handleGeneralChange("transporterNationality", e.target.value)}
            />
          </Field>
          <Field label="Point of loading" error={showErrors && !manifest.general.placeOfDepartureCode}>
            <input
              value={manifest.general.placeOfDepartureCode}
              onChange={(e) => handlePlaceOfDepartureChange(e.target.value)}
              list="pointOfLoading-options"
            />
            <datalist id="pointOfLoading-options">
              {PREDEFINED_TRIPS.map(trip => (
                <option key={trip.pointOfLoading} value={trip.pointOfLoading} />
              ))}
            </datalist>
          </Field>
          <Field label="Point of unloading" error={showErrors && !manifest.general.placeOfDestinationCode}>
            <input
              value={manifest.general.placeOfDestinationCode}
              onChange={(e) => handleGeneralChange("placeOfDestinationCode", e.target.value)}
            />
          </Field>
        </div>
      )}
      </div>

      <div className="card bol-card">
        <div className="card-header-row">
          <h3>AWB Data</h3>
          <button type="button" onClick={handleAddBol}
          className={`interactive ${loading ? "loading" : ""}`}
          >
            + Add New AWB
          </button>
        </div>
        {manifest.bols.length === 0 && (
          <p className="hint-text">No AWB data yet. You can load from an XML file or add AWB manually.</p>
        )}
        <div className="bol-list">
          {manifest.bols.map((master, mIndex) => {
            if (master.hidden) return null;
            
            return (
            <div
              className={`bol-item ${focusedBol === `bol-item-${mIndex}` ? "focused" : ""}`}
              key={master.id || master.tempId || mIndex}
              id={`bol-item-${mIndex}`}
              style={{
                minHeight: focusedBol === `bol-item-${mIndex}` ? "80vh" : "auto",
                transition: "min-height 0.3s ease"
              }}
              onClick={(e) => {
                if (master.collapsed) {
                   setManifest(prev => {
                      const bols = [...prev.bols];
                      bols[mIndex] = { ...bols[mIndex], collapsed: false };
                      return { ...prev, bols };
                   });
                }
                if (e.target.tagName !== "BUTTON") {
                   if (focusedBol !== `bol-item-${mIndex}`) {
                       setFocusedBol(`bol-item-${mIndex}`);
                       ignoreScrollRef.current = true;
                       e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
                       setTimeout(() => { ignoreScrollRef.current = false; }, 800);
                   }
                }
              }}
            >
<div className="card-header-row">
  <div className="bol-title">MASTER AWB #{mIndex + 1}</div>

  <div style={{ display: "flex", gap: 8 }}>
    {master.showSuccessIcon ? (
      <div style={{ display: "flex", alignItems: "center", color: "green", fontWeight: "bold", padding: "0 10px" }}>
        ✅ Saved
      </div>
    ) : (
    <button
      type="button"
      className="secondary-btn"
      onClick={async () => {
        const ok = await handleSave(true);
        if (ok) {
          setManifest((prev) => {
            const bols = [...prev.bols];
            bols[mIndex] = { ...bols[mIndex], showSuccessIcon: true };
            return { ...prev, bols };
          });
          setTimeout(() => {
            setManifest((prev) => {
              const bols = [...prev.bols];
              if (bols[mIndex]) {
                bols[mIndex] = { ...bols[mIndex], hidden: true, showSuccessIcon: false };
              }
              return { ...prev, bols };
            });
          }, 2000);
        }
      }}
    >
      Save & Hide
    </button>
    )}

    <button type="button" onClick={() => handleAddHome(mIndex)}>
      + Add House
    </button>
    {/* ✅ Delete Master Button */}
    <button type="button" className="danger-btn" onClick={() => handleDeleteBol(mIndex)}>
      Delete
    </button>
  </div>
</div>
            {!master.collapsed && (
            <>
              <div className="grid grid-auto small-gap">
                <Field label="AWB Reference" error={showErrors && !master.bolReference}>
                  <input
                    value={master.bolReference}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "bolReference", e.target.value)}
                  />
                </Field>
                {/* <Field label="Flight Number">
                  <input
                    value={master.lineNumber}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "lineNumber", e.target.value)}
                  />
                </Field> */}
                <Field label="Nature of goods" error={showErrors && !master.bolNature}>
                  <input
                    value={master.bolNature}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "bolNature", e.target.value)}
                  />
                </Field>
                <Field label="AWB Type Code" error={showErrors && !master.bolTypeCode}>
                  <input
                    value={master.bolTypeCode}
                    disabled={true} // مقفل دائماً للتعديل اليدوي
                    placeholder="740/741 Auto"
                  />
                </Field>
                {/* <Field label="Point of loading">
                  <input
                    value={master.placeOfLoadingCode}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "placeOfLoadingCode", e.target.value)}
                  />
                </Field> */}
                {/* <Field label="Place of Unloading Code">
                  <input
                    value={master.placeOfUnloadingCode}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "placeOfUnloadingCode", e.target.value)}
                  />
                </Field> */}
              </div>
              <div className="grid grid-auto small-gap">
                {/* ✅ Shipper Autocomplete */}
                <LocalAutocomplete
                  label="Shipper name"
                  storageKey="shippers"
                  value={master.exporterName}
                  error={showErrors && !master.exporterName}
                  disabled={master.isLocked}
                  onChange={(val) => handleBolChange(mIndex, "exporterName", val)}
                  onSelect={(s) => {
                    handleBolChange(mIndex, "exporterName", s.name);
                    handleBolChange(mIndex, "exporterAddress", s.address);
                  }}
                />
                <Field label="Shipper Address" error={showErrors && !master.exporterAddress}>
                  <input
                    value={master.exporterAddress}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "exporterAddress", e.target.value)}
                  />
                </Field>
                {/* ✅ Consignee Autocomplete */}
                <LocalAutocomplete
                  label="Consignee Name"
                  storageKey="consignees"
                  value={master.consigneeName}
                  error={showErrors && !master.consigneeName}
                  disabled={master.isLocked}
                  onChange={(val) => handleBolChange(mIndex, "consigneeName", val)}
                  onSelect={(s) => {
                    handleBolChange(mIndex, "consigneeName", s.name);
                    handleBolChange(mIndex, "consigneeAddress", s.address);
                  }}
                />
                <Field label="Consignee Address" error={showErrors && !master.consigneeAddress}>
                  <input
                    value={master.consigneeAddress}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "consigneeAddress", e.target.value)}
                  />
                </Field>
                <Field label="Notify Name" error={showErrors && !master.notifyName}>
                  <input
                    value={master.notifyName}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "notifyName", e.target.value)}
                  />
                </Field>
                <Field label="Notify Address" error={showErrors && !master.notifyAddress}>
                  <input
                    value={master.notifyAddress}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "notifyAddress", e.target.value)}
                  />
                </Field>
              </div>
              <div className="grid grid-auto small-gap" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                <Field label="Number of Packages" error={showErrors && !master.numberOfPackages}>
                  <input
                    value={master.numberOfPackages}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "numberOfPackages", e.target.value)}
                  />
                </Field>
                <Field label="Package Type Code" error={showErrors && !master.packageTypeCode}>
                  <input
                    value={master.packageTypeCode}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "packageTypeCode", e.target.value)}
                  />
                </Field>
                <Field label="Gross weight" error={showErrors && !master.grossMass}>
                  <input
                    value={master.grossMass}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "grossMass", e.target.value)}
                  />
                </Field>
                <Field label="Shipping Marks" error={showErrors && !master.shippingMarks}>
                  <input
                    value={master.shippingMarks}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "shippingMarks", e.target.value)}
                  />
                </Field>
                <LocalAutocomplete
                  label="Nature of goods"
                  storageKey="goods_descriptions"
                  value={master.goodsDescription}
                  error={showErrors && !master.goodsDescription}
                  disabled={master.isLocked}
                  onChange={(val) => handleBolChange(mIndex, "goodsDescription", val)}
                  onSelect={(s) => {
                    handleBolChange(mIndex, "goodsDescription", s.name);
                  }}
                />
                <Field
                  label={
                    Array.isArray(master.homes) && master.homes.length > 0
                      ? "Service (not required — Consolidation Master)"
                      : "Service"
                  }
                  error={showErrors && !(Array.isArray(master.homes) && master.homes.length > 0) && !master.serviceId}
                >
                  <div className="select-field">
                    {/*  ✅ automatic service selection */}
                    <select
                      value={master.serviceId || ""}
                      disabled={master.isLocked}
                      onChange={(e) => handleBolChange(mIndex, "serviceId", e.target.value)}
                    >
                      <option value="">-- Not selected --</option>
                      {servicesList.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </Field>
                 <div className="field-checkbox" style={{ alignSelf: "end", marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    id={`day-${mIndex}`}
                    checked={master.day || false}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "day", e.target.checked)}
                  />
                  <label htmlFor={`day-${mIndex}`}>Subtract Day</label>
                </div>
                {/* <Field label="Number of Containers for this AWB">
                  <input
                    value={master.numOfCtnForThisBol}
                    disabled={master.isLocked}
                    onChange={(e) => handleBolChange(mIndex, "numOfCtnForThisBol", e.target.value)}
                  />
                </Field> */}
              </div>
            </>
            )}

              {(Array.isArray(master.homes) ? master.homes : []).map((home, hIndex) => {
                if (home.hidden) return null;

                return (
                <div
                  className={`bol-item ${focusedBol === `bol-item-${mIndex}-${hIndex}` ? "focused" : ""}`}
                  key={home.id || home.tempId || `${mIndex}-${hIndex}`}
                  id={`bol-item-${mIndex}-${hIndex}`}
                  style={{
                    marginTop: 12, borderStyle: "dashed",
                    minHeight: focusedBol === `bol-item-${mIndex}-${hIndex}` ? "80vh" : "auto",
                    transition: "min-height 0.3s ease"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (home.collapsed) {
                       setManifest(prev => {
                          const bols = [...prev.bols];
                          const homes = [...bols[mIndex].homes];
                          homes[hIndex] = { ...homes[hIndex], collapsed: false };
                          bols[mIndex] = { ...bols[mIndex], homes };
                          return { ...prev, bols };
                       });
                    }
                    if (e.target.tagName !== "BUTTON") {
                       if (focusedBol !== `bol-item-${mIndex}-${hIndex}`) {
                           setFocusedBol(`bol-item-${mIndex}-${hIndex}`);
                           ignoreScrollRef.current = true;
                           e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
                           setTimeout(() => { ignoreScrollRef.current = false; }, 800);
                       }
                    }
                  }}
                >
                  <div className="bol-title">
  House AWB #{mIndex + 1}.{hIndex + 1}
  {home.showSuccessIcon ? (
    <span style={{ marginLeft: 8, color: "green", fontWeight: "bold" }}>✅ Saved</span>
  ) : (
  <button
    type="button"
    className="secondary-btn"
    style={{ marginLeft: 8 }}
    onClick={async () => {
      const ok = await handleSave(true);
      if (ok) {
        setManifest((prev) => {
          const bols = [...prev.bols];
          const homes = [...bols[mIndex].homes];
          homes[hIndex] = { ...homes[hIndex], showSuccessIcon: true };
          bols[mIndex] = { ...bols[mIndex], homes };
          return { ...prev, bols };
        });
        setTimeout(() => {
          setManifest((prev) => {
            const bols = [...prev.bols];
            if (bols[mIndex] && bols[mIndex].homes && bols[mIndex].homes[hIndex]) {
              const homes = [...bols[mIndex].homes];
              homes[hIndex] = { ...homes[hIndex], hidden: true, showSuccessIcon: false };
              bols[mIndex] = { ...bols[mIndex], homes };
            }
            return { ...prev, bols };
          });
        }, 2000);
      }
    }}
  >
    Save & Hide
  </button>
  )}
  {/* ✅ Delete House Button */}
  <button type="button" className="danger-btn" style={{ marginLeft: 8 }} onClick={() => handleDeleteHome(mIndex, hIndex)}>
    Delete
  </button>
</div>
                  {!home.collapsed && (
                  <>
                  <div className="grid grid-auto small-gap">
                    <Field label="AWB Reference" error={showErrors && !home.bolReference}>
                      <input
                        value={home.bolReference}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "bolReference", e.target.value, hIndex)}
                      />
                    </Field>
                    {/* <Field label="Flight Number">
                      <input
                        value={home.lineNumber}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "lineNumber", e.target.value, hIndex)}
                      />
                    </Field> */}
                    <Field label="Nature of goods" error={showErrors && !home.bolNature}>
                      <input
                        value={home.bolNature}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "bolNature", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="AWB Type Code" error={showErrors && !home.bolTypeCode}>
                      <input
                        value={home.bolTypeCode}
                        disabled={true} // مقفل دائماً
                        placeholder="740"
                      />
                    </Field>
                    {/* <Field label="Point of loading">
                      <input
                        value={home.placeOfLoadingCode}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "placeOfLoadingCode", e.target.value, hIndex)}
                      />
                    </Field> */}
                    {/* <Field label="Place of Unloading Code">
                      <input
                        value={home.placeOfUnloadingCode}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "placeOfUnloadingCode", e.target.value, hIndex)}
                      />
                    </Field> */}
                  </div>
                  <div className="grid grid-auto small-gap">
                    {/* ✅ House Shipper Autocomplete */}
                    <LocalAutocomplete
                      label="Shipper name"
                      storageKey="shippers"
                      value={home.exporterName}
                      error={showErrors && !home.exporterName}
                      disabled={home.isLocked}
                      onChange={(val) => handleBolChange(mIndex, "exporterName", val, hIndex)}
                      onSelect={(s) => {
                        handleBolChange(mIndex, "exporterName", s.name, hIndex);
                        handleBolChange(mIndex, "exporterAddress", s.address, hIndex);
                      }}
                    />
                    <Field label="Shipper Address" error={showErrors && !home.exporterAddress}>
                      <input
                        value={home.exporterAddress}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "exporterAddress", e.target.value, hIndex)}
                      />
                    </Field>
                    {/* ✅ House Consignee Autocomplete */}
                    <LocalAutocomplete
                      label="Consignee Name"
                      storageKey="consignees"
                      value={home.consigneeName}
                      error={showErrors && !home.consigneeName}
                      disabled={home.isLocked}
                      onChange={(val) => handleBolChange(mIndex, "consigneeName", val, hIndex)}
                      onSelect={(s) => {
                        handleBolChange(mIndex, "consigneeName", s.name, hIndex);
                        handleBolChange(mIndex, "consigneeAddress", s.address, hIndex);
                      }}
                    />
                    <Field label="Consignee Address" error={showErrors && !home.consigneeAddress}>
                      <input
                        value={home.consigneeAddress}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "consigneeAddress", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Notify Name" error={showErrors && !home.notifyName}>
                      <input
                        value={home.notifyName}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "notifyName", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Notify Address" error={showErrors && !home.notifyAddress}>
                      <input
                        value={home.notifyAddress}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "notifyAddress", e.target.value, hIndex)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-auto small-gap" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                    <Field label="Number of Packages">
                      <input
                        value={home.numberOfPackages}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "numberOfPackages", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Package Type Code">
                      <input
                        value={home.packageTypeCode}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "packageTypeCode", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Gross weight">
                      <input
                        value={home.grossMass}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "grossMass", e.target.value, hIndex)}
                      />
                    </Field>
                    <Field label="Shipping Marks">
                      <input
                        value={home.shippingMarks}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "shippingMarks", e.target.value, hIndex)}
                      />
                    </Field>
                    <LocalAutocomplete
                      label="Nature of goods"
                      storageKey="goods_descriptions"
                      value={home.goodsDescription}
                      disabled={home.isLocked}
                      onChange={(val) => handleBolChange(mIndex, "goodsDescription", val, hIndex)}
                      onSelect={(s) => {
                        handleBolChange(mIndex, "goodsDescription", s.name, hIndex);
                      }}
                    />
                    <Field label="Service">
                      <div className="select-field">
                        <select
                          value={home.serviceId || ""}
                          disabled={home.isLocked}
                          onChange={(e) => handleBolChange(mIndex, "serviceId", e.target.value, hIndex)}
                        >
                          <option value="">-- Not selected --</option>
                          {servicesList.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    </Field>
                             <div className="field-checkbox" style={{ alignSelf: "end", marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        id={`day-${mIndex}-${hIndex}`}
                        checked={home.day || false}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "day", e.target.checked, hIndex)}
                      />
                      <label htmlFor={`day-${mIndex}-${hIndex}`}>Subtract Day</label>
                    </div>
                    {/* <Field label="Number of Containers for this AWB">
                      <input
                        value={home.numOfCtnForThisBol}
                        disabled={home.isLocked}
                        onChange={(e) => handleBolChange(mIndex, "numOfCtnForThisBol", e.target.value, hIndex)}
                      />
                    </Field> */}
                  </div>
                  </>
)}
                </div>
                

                );
              })}

            </div>


            );
          })}

        </div>

      </div>

      {message.text && (
        <div className={`message ${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="actions-row" style={{ display: "flex", gap: "10px" }}>
        <button
          type="button"
          className="secondary-btn"
          onClick={handleExportXml}
        >
          Export XML
        </button>
<button
  type="button"
  onClick={async () => {
        const ok = await handleSave();
        if (ok) resetForm();
  }}
  disabled={loading || (selectedManifestId ? !canEditManifest : !canCreateManifest)}
>
  {loading ? "Saving..." : "Save to Database"}
</button>

      </div>
    </div>
  );
}

export default ManifestScreen;
